import { Trans, useLingui } from '@lingui/react/macro'
import { LucideIcon } from 'components/LucideIcon'
import { Button } from 'designSystem/components/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import { useIsMobile } from 'designSystem/hooks/useMobile'
import { useSheetPortalHost } from 'designSystem/hooks/useSheetPortalHost'
import type {
  ChatHistoryBulkAction,
  ChatHistorySection,
} from 'features/embeddedSidebar/sessions/chatHistoryBulkActions'
import { type MouseEvent, useCallback, useState } from 'react'

import { HistoryHeaderAction } from './agenticChatHistorySidebarStyles'
import { ChatHistoryBulkActionDialog } from './ChatHistoryBulkActionDialog'

interface ChatHistoryOverflowMenuProps {
  section: ChatHistorySection
  disabled: boolean
  onAction: (action: ChatHistoryBulkAction) => Promise<boolean>
}

export function ChatHistoryOverflowMenu({
  disabled,
  section,
  onAction,
}: ChatHistoryOverflowMenuProps) {
  const { t } = useLingui()
  const menuLabel =
    section === 'recent' ? t`Recent chat actions` : t`Archived chat actions`
  const isMobile = useIsMobile()
  const portalHost = useSheetPortalHost(isMobile)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [action, setAction] = useState<ChatHistoryBulkAction | null>(null)
  const [isWorking, setIsWorking] = useState(false)
  const [hasError, setHasError] = useState(false)

  const startAction = useCallback(
    (nextAction: ChatHistoryBulkAction, event: Event) => {
      event.preventDefault()
      setIsMenuOpen(false)
      setHasError(false)
      window.setTimeout(() => setAction(nextAction), 0)
    },
    []
  )
  const handleArchive = useCallback(
    (event: Event) => startAction('archive', event),
    [startAction]
  )
  const handleDelete = useCallback(
    (event: Event) => startAction('delete', event),
    [startAction]
  )
  const handleUnarchive = useCallback(
    (event: Event) => startAction('unarchive', event),
    [startAction]
  )
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isWorking) setAction(null)
    },
    [isWorking]
  )
  const handleConfirm = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      if (!action || isWorking) return
      setIsWorking(true)
      setHasError(false)
      const succeeded = await onAction(action)
      setIsWorking(false)
      if (succeeded) setAction(null)
      else setHasError(true)
    },
    [action, onAction, isWorking]
  )

  return (
    <>
      <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <HistoryHeaderAction data-open={isMenuOpen || undefined}>
          <DropdownMenuTrigger asChild>
            <Button
              aria-label={menuLabel}
              disabled={disabled}
              size='icon-sm'
              type='button'
              variant='ghostMuted'>
              <LucideIcon aria-hidden='true' name='EllipsisVertical' />
            </Button>
          </DropdownMenuTrigger>
        </HistoryHeaderAction>
        {portalHost.hostNode}
        <DropdownMenuContent
          align='start'
          side={isMobile ? 'bottom' : 'right'}
          width='sm'
          container={portalHost.container}>
          {section === 'recent' ? (
            <DropdownMenuItem onSelect={handleArchive}>
              <LucideIcon aria-hidden='true' name='Archive' />
              <Trans>Archive all</Trans>
            </DropdownMenuItem>
          ) : (
            <>
              <DropdownMenuItem onSelect={handleUnarchive}>
                <LucideIcon aria-hidden='true' name='ArchiveRestore' />
                <Trans>Unarchive all</Trans>
              </DropdownMenuItem>
              <DropdownMenuItem variant='destructive' onSelect={handleDelete}>
                <LucideIcon aria-hidden='true' name='Trash2' />
                <Trans>Delete all</Trans>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {action ? (
        <ChatHistoryBulkActionDialog
          action={action}
          hasError={hasError}
          isWorking={isWorking}
          onConfirm={handleConfirm}
          onOpenChange={handleOpenChange}
        />
      ) : null}
    </>
  )
}
